import { Grid, Typography, Paper, Box, useTheme, alpha, Card, CardContent } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CreateShipmentContext from '../../../../context/CreateShipment';
import { createShipmentContextType } from '../../../../types/shipment';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { isEUCountry } from '../../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../../utils/currencyConversion';
import { getCountryName } from '../../../../utils/exportShipment';
import {
  calculateBoxWiseChargeableWt,
  calculateChargeableWt,
  kgToLb,
  twoDecimalPlace,
} from '../../../../utils/measurements';

interface PropsType {
  label: string;
  value: string;
  subLabel?: string;
  icon: React.ReactNode;
}

function TotalValue({ label, value, subLabel, icon }: PropsType) {
  const theme = useTheme();
  return (
    <Grid item xs={12} sm={4}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: '12px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.02)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.05)',
            borderColor: alpha(theme.palette.primary.main, 0.35),
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: theme.palette.text.primary,
              letterSpacing: '0.3px',
              display: 'block',
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Typography>
          {subLabel && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {subLabel}
            </Typography>
          )}
        </Box>
      </Paper>
    </Grid>
  );
}

export default function TotalValues() {
  const theme = useTheme();
  const {
    totalShipmentValue,
    boxes,
    chargeableWeight,
    actualWeight,
    shipmentType,
    shipmentById,
    setChargeableWeight,
    consignee,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const { chareableWeight } = useSelector((state: RootStateType) => state.getRates);
  const unit = shipmentType.unit.split('-')[0];
  const activeServices = useSelector((state: RootStateType) => state.carrierAndService.serviceList);
  const adminShipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const orgCountryCurrency = useSelector((state: RootStateType) => state.orgSetup.organizationData?.currency?.symbol);
  const { minWt, maxWt } = useMemo(
    function () {
      let minWt = Infinity;
      let maxWt = -Infinity;
      activeServices.forEach(function (csp) {
        const isBoxWise = csp.boxWiseRates;
        if (isBoxWise) {
          const chargeableWt = calculateBoxWiseChargeableWt(
            boxes,
            csp.dimWtDivisor.kg,
            shipmentType.unit === 'lb-in',
          ).reduce((total, obj) => {
            return total + obj.chargeableWeight;
          }, 0);
          minWt = chargeableWt < minWt ? chargeableWt : minWt;
          maxWt = chargeableWt > maxWt ? chargeableWt : maxWt;
        } else {
          const { chargeableWeight } = calculateChargeableWt(
            0,
            boxes,
            csp.dimWtDivisor.kg,
            shipmentType.unit === 'lb-in',
          );
          minWt = Number(chargeableWeight) < minWt ? Number(chargeableWeight) : minWt;
          maxWt = Number(chargeableWeight) > maxWt ? Number(chargeableWeight) : maxWt;
        }
      });
      return {
        minWt: twoDecimalPlace(kgToLb(minWt, shipmentType.unit === 'lb-in')),
        maxWt: twoDecimalPlace(kgToLb(maxWt, shipmentType.unit === 'lb-in')),
      };
    },
    [boxes, shipmentType.unit],
  );
  const activeShipment = adminShipmentById || shipmentById;
  const destCountry = getCountryName(consignee) || getCountryName(activeShipment?._consigneeId) || '';
  const isDestEU = isEUCountry(destCountry);
  const { rate: eurRate } = useEurExchangeRate({ currency: shipmentType?.currency, isEU: isDestEU });
  const eurEquivalentText = useMemo(() => {
    if (!isDestEU || !eurRate || (shipmentType?.currency || '').toUpperCase() === 'EUR') return '';
    const eurVal = (totalShipmentValue * eurRate).toFixed(2);
    return `(≈ €${eurVal})`;
  }, [isDestEU, eurRate, shipmentType?.currency, totalShipmentValue]);

  const minChargeableWt = chareableWeight?.minChargeableWt ? chareableWeight?.minChargeableWt : 0;
  const maxChargeableWt = chareableWeight?.maxChargeableWt ? chareableWeight?.maxChargeableWt : 0;

  let displayChargeableWt = activeShipment
    ? `${chareableWeight?.maxChargeableWt} ${unit}`
    : `${minChargeableWt} - ${maxChargeableWt} ${unit}`;
  if (minWt === maxWt && !shipmentById) displayChargeableWt = minWt + ' Kg';

  console.log(displayChargeableWt);

  useEffect(() => {
    if (activeShipment && chareableWeight?.maxChargeableWt) {
      setChargeableWeight(chareableWeight?.maxChargeableWt);
    }
  }, [chargeableWeight]);

  const selectedUnit = useMemo(() => unit.split('-')[0], [unit]);

  const pathname = location.pathname;

  const isShipmentEditOrHistory =
    /^\/shipment\/admin\/edit\/[^/]+$/.test(pathname) || /^\/shipment\/history\/[^/]+$/.test(pathname);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: theme.palette.background.paper,
        width: '100%',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ScaleOutlinedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
              Shipment Value & Weight
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
              Customs declaration value, billed chargeable weight, and actual scale weight
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Section Body */}
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Grid container spacing={2}>
          {/* 1. Shipment Value */}
          <TotalValue
            label="Shipment Value"
            value={`${shipmentType.currency || orgCountryCurrency} ${totalShipmentValue.toFixed(2)}${
              eurEquivalentText ? ` ${eurEquivalentText}` : ''
            }`}
            subLabel={isDestEU ? 'Declared Customs Value · €3 duty per line' : 'Declared Customs Value'}
            icon={<PaymentsOutlinedIcon />}
          />

          {/* 2. Chargeable Weight */}
          <TotalValue
            label="Chargeable Weight"
            subLabel="Billed by Carrier"
            icon={<FitnessCenterOutlinedIcon />}
            value={
              isShipmentEditOrHistory
                ? `${
                    (adminShipmentById?.sellingPricing as any)?.displayChargeableWeight ?? (0).toFixed(2)
                  } ${selectedUnit}`
                : `${
                    chareableWeight?.boxWiseArr
                      ? chareableWeight.minChargeableWt != null &&
                        chareableWeight.maxChargeableWt != null &&
                        chareableWeight.minChargeableWt === chareableWeight.maxChargeableWt
                        ? selectedUnit === 'lb'
                          ? (chareableWeight.maxChargeableWt * 2.20462).toFixed(2)
                          : chareableWeight.maxChargeableWt.toFixed(2)
                        : `${
                            selectedUnit === 'lb'
                              ? ((chareableWeight.minChargeableWt ?? 0) * 2.20462).toFixed(2)
                              : (Number(chareableWeight.minChargeableWt) || 0).toFixed(2)
                          } - ${
                            selectedUnit === 'lb'
                              ? ((chareableWeight.maxChargeableWt ?? 0) * 2.20462).toFixed(2)
                              : (chareableWeight.maxChargeableWt ?? 0).toFixed(2)
                          }`
                      : (0).toFixed(2)
                  } ${selectedUnit}`
            }
          />

          {/* 3. Actual Weight */}
          <TotalValue
            label="Actual Weight"
            value={`${parseFloat(`${actualWeight ? actualWeight : 0}`).toFixed(2)} ${unit}`}
            subLabel="Scale Weight"
            icon={<ScaleOutlinedIcon />}
          />
        </Grid>
      </CardContent>
    </Card>
  );
}
